// @flow

import React from 'react';
import { Translate } from 'react-redux-i18n';
import { Cookies, withCookies } from 'react-cookie';
import { connect } from 'react-redux';
import Loader from '../../../common/Loader';
import RoiKpi from './RoiKpi';
import RoiKpiParamField from './RoiKpiParamField';
import {
  computePayback,
  formatMgr,
  formatPayback,
  formatRoi,
  formatRoiParam,
  formatSuggestionMgr,
  formatSuggestionTic,
  formatTic,
} from '../../../../utils/formatUtils';
import {
  getConvertedValueFromCurrentCurrency,
  undoConvertedValueFromCurrentCurrency,
} from '../../../../utils/currencyConvertorUtils';
import { deepCompare } from '../../../../utils/utils';
import type { RoiConstraints, RoiParams, RoiResult } from '../../../../types/apolloType';
import storesApi from '../../../../network/api/storesApi';
import { defaultCurrency, twCurrency } from '../../../../const';
import type { SelectConvertorMap } from '../../../../types/commonTypes';

type Props = {
  title: string,
  titleDetails: string,
  roiSuggestionResult: ?RoiResult,
  roiConstraints: RoiConstraints,
  showDetails: (boolean, RoiResult) => (void),
  codstr: number,
  cookies: Cookies,
  currenciesList: SelectConvertorMap,
}

type State = {
  initialRoiSuggestion: RoiResult,
  currentRoiSuggestion: RoiResult,
  roiParams: RoiParams,
  recomputeSuggestionIsLoading: boolean,
  newMgr: boolean,
  keyMoney: boolean,
  rentIncentives: boolean,
}

const euroPattern = '[0-9 ]+';

const formatSuggestionResult = (roiSuggestionResult: RoiResult) => {
  if (!roiSuggestionResult) {
    return roiSuggestionResult;
  }

  const { roiParams } = roiSuggestionResult;
  return {
    ...roiSuggestionResult,
    ...roiParams,
    mgrUplift: formatSuggestionMgr(roiSuggestionResult.netMgrGlaUplift),
    tic: formatSuggestionTic(roiSuggestionResult.tic),
    newMgr: formatSuggestionMgr(roiParams.newMgr),
    keyMoney: formatSuggestionTic(roiParams.keyMoney),
    rentIncentives: formatSuggestionTic(roiParams.rentIncentives),
  };
};

class RoiPanelResultForm extends React.Component<Props, State> {
  state = {
    currentRoiSuggestion: formatSuggestionResult(this.props.roiSuggestionResult),
    roiParams: formatSuggestionResult(this.props.roiSuggestionResult),
    recomputeSuggestionIsLoading: false,
    newMgr: false,
    keyMoney: false,
    rentIncentives: false,
  }

  fixedRoiParamsKey = (key: string): string => `${key}Present`;

  onSubmit = (e) => {
    e.preventDefault();
    this.setState({
      recomputeSuggestionIsLoading: true,
    });
    const { currentRoiSuggestion, roiParams } = this.state;
    Object.keys(currentRoiSuggestion)
      .forEach((key) => {
        const isLocked = (this.state.hasOwnProperty(key) && typeof this.state[key] === 'boolean') ? this.state[key] : false;
        roiParams.fixedRoiParams[this.fixedRoiParamsKey(key)] = isLocked;
      });

    const roiCustomFocusBean = {
      roiParams,
      roiCustomFocusConstraints: this.props.roiConstraints,
    };
    storesApi.computeCustomFocusRoi(
      this.props.codstr,
      roiCustomFocusBean,
    ).then(resp => resp.json())
      .then(roiSuggestion => this.setState({
        currentRoiSuggestion: formatSuggestionResult(roiSuggestion),
        roiParams: formatSuggestionResult(roiSuggestion),
      }))
      .catch(console.error())
      .finally(() => this.setState({
        recomputeSuggestionIsLoading: false,
      }));
  }

  onParamsChange = (key: string) => (value) => {
    const { roiParams } = this.state;
    roiParams[key] = value;
    this.setState({
      roiParams,
    });
  }

  isParamOutOfConstraints = (paramName: string): boolean => {
    const paramValue = this.state.roiParams[paramName];
    const paramConstraints = this.props.roiConstraints[paramName];
    return paramValue > paramConstraints.max || paramValue < paramConstraints.min;
  }

  resetForm(): void {
    this.setState({
      currentRoiSuggestion: this.props.roiSuggestionResult,
      roiParams: formatSuggestionResult(this.props.roiSuggestionResult),
      newMgr: false,
      keyMoney: false,
      rentIncentives: false,
    });
  }

  getCurrentCurrencyUnit(): string {
    const currency = this.props.cookies.get(twCurrency) || defaultCurrency;
    const currentCurrency = this.props.currenciesList[currency];
    return currentCurrency.symbol;
  }

  convertValueToCurrency = (value: number | string): string => getConvertedValueFromCurrentCurrency(
    value,
    this.props.currenciesList,
    this.props.cookies.get(twCurrency),
  );

  undoConvertValueToCurrency = (value: number | string): number =>
    undoConvertedValueFromCurrentCurrency(
      value.replace(/\s+/g, ''),
      this.props.currenciesList,
      this.props.cookies.get(twCurrency),
    );

  getContent(): any {
    if (this.state.recomputeSuggestionIsLoading) {
      return (
        <Loader isLoading message={<Translate value="roi.computing" />} />
      );
    }
    if (this.state.roiParams) {
      return (
        <form onSubmit={this.onSubmit} className="roi-inputs-container">
          <div className="roi-result">
            {
              deepCompare(
                formatSuggestionResult(this.props.roiSuggestionResult),
                this.state.roiParams,
              )
              && (
                <button
                  type="button"
                  className="apollo-button-contained reset-button panel-result-button"
                  onClick={() => this.resetForm()}
                >
                  <div className="filter-bar-big-text">
                    <Translate value="roi.reset" />
                  </div>
                </button>
              )
            }
            <div className="custom-roi-inputs-container">
              <RoiKpi
                kpiValue={formatRoi(
                  this.state.currentRoiSuggestion.roi,
                  this.state.currentRoiSuggestion.netMgrGlaUplift,
                  this.state.currentRoiSuggestion.tic,
                )}
                isFocus={false}
              />
              <RoiKpi
                kpiValue={
                  formatPayback(computePayback(
                    this.state.currentRoiSuggestion.netMgrGlaUplift,
                    this.state.currentRoiSuggestion.tic,
                  ))
                }
                isFocus={false}
              />
              <RoiKpiParamField
                kpiParam={this.state.roiParams.newMgr}
                onChange={this.onParamsChange('newMgr')}
                isFocus={false}
                hideInfoBox={false}
                pattern={euroPattern}
                convertValueToCurrency={this.convertValueToCurrency}
                undoConvertValueToCurrency={this.undoConvertValueToCurrency}
                currencyUnit={this.getCurrentCurrencyUnit()}
                locked={this.state.newMgr}
                setLocked={() => this.setState({ newMgr: !this.state.newMgr })}
                showTooltipWarning={this.isParamOutOfConstraints('newMgr')}
              />
              <RoiKpi
                kpiValue={formatMgr(this.state.roiParams.mgrUplift)}
                isFocus={false}
              />
              <RoiKpi
                kpiValue={formatTic(this.state.roiParams.tic)}
                isFocus={false}
              />
              <RoiKpiParamField
                kpiParam={this.state.roiParams.keyMoney}
                onChange={this.onParamsChange('keyMoney')}
                isFocus={false}
                hideInfoBox={false}
                pattern={euroPattern}
                convertValueToCurrency={this.convertValueToCurrency}
                undoConvertValueToCurrency={this.undoConvertValueToCurrency}
                currencyUnit={this.getCurrentCurrencyUnit()}
                locked={this.state.keyMoney}
                setLocked={() => this.setState({ keyMoney: !this.state.keyMoney })}
                showTooltipWarning={this.isParamOutOfConstraints('keyMoney')}
              />
              <RoiKpi
                kpiValue={formatRoiParam(this.state.roiParams.valueCreationVsCurrent)}
                isFocus={false}
              />
              <RoiKpiParamField
                kpiParam={this.state.roiParams.rentIncentives}
                onChange={this.onParamsChange('rentIncentives')}
                isFocus={false}
                hideInfoBox={false}
                pattern={euroPattern}
                convertValueToCurrency={this.convertValueToCurrency}
                undoConvertValueToCurrency={this.undoConvertValueToCurrency}
                currencyUnit={this.getCurrentCurrencyUnit()}
                locked={this.state.rentIncentives}
                setLocked={() =>
                  this.setState({ rentIncentives: !this.state.rentIncentives })
                }
                showTooltipWarning={this.isParamOutOfConstraints('rentIncentives')}
              />
            </div>
          </div>
          <div className="roi-button-container roi-button-container-result">
            <button
              type="button"
              className="apollo-button-contained details-button panel-result-button"
              onClick={() => this.props.showDetails(true, this.state.currentRoiSuggestion)}
            >
              <div className="button-big-text">
                <Translate value="roi.details" />
              </div>
            </button>
            <button
              type="submit"
              className="apollo-button-contained panel-result-button"
            >
              <div className="button-big-text">
                <Translate value="roi.compute" />
              </div>
            </button>
          </div>
        </form>
      );
    }
    return (
      <div className="roi-result" />
    );
  }

  render() {
    return (
      <div className="roi-panel roi-panel-kpi-form">
        <h3 className="roi-subtitle">
          <Translate value={this.props.title} />
          <Translate className="subtitle-details" value={this.props.titleDetails} />
        </h3>
        <div className="roi-panel-content roi-panel-result-form">
          {this.getContent()}
        </div>
      </div>
    );
  }
}

export default withCookies(connect(state => ({
  currenciesList: state.dynamicKeys.currenciesList,
}))(RoiPanelResultForm));
